// Implements: F-IDN-010, F-UIX-009, F-UIX-013
/**
 * "Cerrar las demás sesiones": asks first with `ConfirmDialog` (never `confirm()`), sends one
 * `POST /auth/sessions/revoke-others` (never retried, checklist 13) and says how many the SERVER
 * closed, which is the number it answered and not the number of rows on screen. The list is
 * refreshed afterwards by the hook. This device is never one of them (the server keeps it).
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { SessionSummary } from "../../../types/api";
import { ConfirmDialog } from "../../../shared/ui/molecules/ConfirmDialog";
import { useToast } from "../../../shared/ui/molecules/ToastProvider";
import { SessionsToolbar } from "../components/SessionsToolbar";
import { useRevokeOthers } from "../hooks/useRevokeOthers";

export interface SessionsToolbarContainerProps {
  readonly sessions: readonly SessionSummary[];
}

export function SessionsToolbarContainer({ sessions }: SessionsToolbarContainerProps): ReactElement {
  const { t } = useTranslation();
  const revokeOthers = useRevokeOthers();
  const { push } = useToast();
  const [asking, setAsking] = useState(false);
  const others = sessions.filter((session) => !session.current).length;

  const closedText = (count: number): string => {
    if (count === 0) return t("account.sessions.closedNone");
    if (count === 1) return t("account.sessions.closedOne");
    return t("account.sessions.closedMany", { count });
  };

  return (
    <>
      <SessionsToolbar
        total={sessions.length}
        others={others}
        busy={revokeOthers.isPending}
        onRevokeOthers={() => {
          setAsking(true);
        }}
      />
      <ConfirmDialog
        open={asking}
        title={t("account.sessions.confirmTitle")}
        body={others === 1 ? t("account.sessions.confirmBodyOne") : t("account.sessions.confirmBodyMany", { count: others })}
        confirmLabel={t("account.sessions.confirm")}
        cancelLabel={t("common.action.cancel")}
        confirmLoading={revokeOthers.isPending}
        onConfirm={() => {
          setAsking(false);
          revokeOthers.mutate(null, {
            onSuccess: ({ revoked }) => {
              push({ tone: "success", message: closedText(revoked) });
            },
            onError: () => {
              push({ tone: "danger", message: t("common.state.error") });
            },
          });
        }}
        onCancel={() => {
          setAsking(false);
        }}
      />
    </>
  );
}
